/**
 * Derivación espacial de colindancias para parcelas.
 *
 * Cuando el GeoJSON `properties.colindancias` no trae texto declarado, este
 * módulo computa los vecinos por punto cardinal (Norte/Sur/Oriente/Poniente)
 * a partir de la geometría — la "segunda fase" documentada en
 * `services/geo/scripts/build_unified_geojsons.py`.
 *
 * Algoritmo (sin dependencias externas, optimizado para mobile):
 *   1. Vertex-share heurístico para detectar adyacencia: dos polígonos
 *      tocan si comparten ≥ `minSharedVertices` vértices con tolerancia
 *      `vertexTol` grados (~10-100 cm). Funciona porque las parcelas RAN
 *      adyacentes comparten endpoints exactos en la fuente KML.
 *   2. Pre-filtro por proximidad de bbox (`maxKm`) para evitar O(N) sobre
 *      todo el catálogo en cada click.
 *   3. Para cada vecino, bearing centroid→centroid en grados (0=N, 90=E,
 *      180=S, 270=O). Bucketing en 4 sectores cardinales de 90°.
 *   4. Si más de un vecino cae en el mismo sector, se ordenan por desviación
 *      respecto al cardinal "perfecto" y se concatenan los 3 más cercanos.
 *
 * Limitaciones conocidas (documentar en UX si se vuelve fuente de verdad):
 *   - Multi-vecinos por lado: usamos los 3 más cercanos al cardinal
 *     dominante. Casos donde la parcela colinda con 5+ vecinos por un solo
 *     lado quedan truncados.
 *   - "Touch de un solo punto" (kiss): por defecto requerimos 1 vértice
 *     compartido. Para ser estricto con "frontera real" subir
 *     `minSharedVertices` a 2.
 *   - Polígonos en lat/lng sin reproyectar: la aproximación es válida para
 *     escalas locales (~km) en latitudes templadas; para grandes extensiones
 *     o cerca de los polos haría falta proyectar a métrico.
 *   - Cuerpos de agua / caminos / uso común no son parcelas → no aparecen
 *     en la lista (este módulo solo recibe `parcelas`).
 */

// Tipos GeoJSON.* vienen del global namespace (provisto por @types/geojson
// transitive); no necesitamos `import { GeoJSON.Feature, GeoJSON.Geometry } from 'geojson'`
// que requiere el paquete instalado explícitamente en este workspace.

export interface ColindanciasSpatialResult {
  norte: string | null;
  sur: string | null;
  oriente: string | null;
  poniente: string | null;
}

export interface ColindanciasSpatialOptions {
  /** Distancia máxima (km) entre bbox centers para considerar candidato.
   *  Default 0.5 — más que suficiente para parcelas adyacentes. */
  maxKm?: number;
  /** Vértices compartidos mínimos para considerar "tocan". 1 detecta toques
   *  esquina; 2 exige frontera real. Default 1 (más permisivo, mejor en
   *  parcelas con vértices sutilmente desplazados). */
  minSharedVertices?: number;
  /** Tolerancia en grados para igualar dos vértices. 1e-6 ≈ 11 cm en
   *  Querétaro. Default 1e-6. */
  vertexTol?: number;
  /** Máximo de etiquetas concatenadas por sector cardinal. Default 3. */
  maxPerSide?: number;
}

type Coord = [number, number];

function isCoord(x: unknown): x is Coord {
  return Array.isArray(x) && typeof x[0] === 'number' && typeof x[1] === 'number';
}

/** Recorre el árbol de coordenadas (Polygon o MultiPolygon) sin recursión
 *  profunda; devuelve un Set de "x,y" redondeados a `tol`. */
function collectVertices(geom: GeoJSON.Geometry, tol: number): Set<string> {
  const out = new Set<string>();
  const round = (v: number) => Math.round(v / tol) * tol;
  const stack: unknown[] = [];
  if ((geom.type === 'Polygon' || geom.type === 'MultiPolygon') && Array.isArray((geom as { coordinates?: unknown }).coordinates)) {
    stack.push((geom as { coordinates: unknown }).coordinates);
  }
  while (stack.length) {
    const cur = stack.pop();
    if (!Array.isArray(cur)) continue;
    if (isCoord(cur)) {
      out.add(`${round(cur[0])},${round(cur[1])}`);
    } else {
      for (const child of cur) stack.push(child);
    }
  }
  return out;
}

/** Bbox en grados [minLng, minLat, maxLng, maxLat]. */
function bboxOf(geom: GeoJSON.Geometry): [number, number, number, number] {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const stack: unknown[] = [];
  if ((geom.type === 'Polygon' || geom.type === 'MultiPolygon') && Array.isArray((geom as { coordinates?: unknown }).coordinates)) {
    stack.push((geom as { coordinates: unknown }).coordinates);
  }
  while (stack.length) {
    const cur = stack.pop();
    if (!Array.isArray(cur)) continue;
    if (isCoord(cur)) {
      const x = cur[0];
      const y = cur[1];
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    } else {
      for (const child of cur) stack.push(child);
    }
  }
  return [minX, minY, maxX, maxY];
}

function bboxCenter(bb: [number, number, number, number]): Coord {
  return [(bb[0] + bb[2]) / 2, (bb[1] + bb[3]) / 2];
}

/** Bearing en grados desde `from` a `to`, con 0°=N, 90°=E, 180°=S, 270°=O.
 *  Aproximación equirectangular suficiente para clasificar en 4 sectores. */
function bearingDeg(from: Coord, to: Coord): number {
  const dy = to[1] - from[1]; // delta latitud → componente N-S
  const dx = to[0] - from[0]; // delta longitud → componente E-O
  const rad = Math.atan2(dx, dy);
  return ((rad * 180) / Math.PI + 360) % 360;
}

type Cardinal = 'norte' | 'oriente' | 'sur' | 'poniente';

function bearingToCardinal(deg: number): Cardinal {
  if (deg < 45 || deg >= 315) return 'norte';
  if (deg < 135) return 'oriente';
  if (deg < 225) return 'sur';
  return 'poniente';
}

const PERFECT: Record<Cardinal, number> = {
  norte: 0,
  oriente: 90,
  sur: 180,
  poniente: 270,
};

/** Distancia angular mínima entre dos bearings, en grados (0..180). */
function angularDelta(a: number, b: number): number {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
}

/** Distancia bbox→bbox en grados (0 si overlap), aproximada a km al lat 20. */
function bboxDistanceKm(
  a: [number, number, number, number],
  b: [number, number, number, number],
): number {
  const dx = Math.max(0, Math.max(a[0] - b[2], b[0] - a[2]));
  const dy = Math.max(0, Math.max(a[1] - b[3], b[1] - a[3]));
  // 1° lat ≈ 111 km; 1° lng ≈ 104 km en Querétaro lat ~20.4.
  const km = Math.sqrt(dx * dx + dy * dy) * 111;
  return km;
}

/** Etiqueta humana para una parcela vecina. Prefiere "Parcela <num> (id)";
 *  fallback al `parcela_id` solo. */
function neighborLabel(props: Record<string, unknown>): string {
  const id = props.parcela_id != null ? String(props.parcela_id) : '';
  const num = props.num_parcela;
  if (num != null && id) return `Parcela ${num} (${id})`;
  if (id) return id;
  return 'Predio sin folio';
}

/**
 * Computa colindancias espaciales del `target` contra `candidates`.
 * Devuelve `undefined` si no encuentra ningún vecino (UX: empty-state se
 * mantiene sin pretender un dato que no se pudo derivar).
 */
export function computeColindanciasSpatial(
  target: GeoJSON.Feature,
  candidates: GeoJSON.Feature[],
  options: ColindanciasSpatialOptions = {},
): ColindanciasSpatialResult | undefined {
  const {
    maxKm = 0.5,
    minSharedVertices = 1,
    vertexTol = 1e-6,
    maxPerSide = 3,
  } = options;

  if (!target.geometry || candidates.length === 0) return undefined;
  const targetProps = (target.properties ?? {}) as Record<string, unknown>;
  const targetId = targetProps.parcela_id != null ? String(targetProps.parcela_id) : '';

  const targetBbox = bboxOf(target.geometry);
  const targetCenter = bboxCenter(targetBbox);
  const targetVerts = collectVertices(target.geometry, vertexTol);

  type Bucketed = { label: string; delta: number };
  const buckets: Record<Cardinal, Bucketed[]> = {
    norte: [],
    oriente: [],
    sur: [],
    poniente: [],
  };

  for (const cand of candidates) {
    if (!cand.geometry) continue;
    const cprops = (cand.properties ?? {}) as Record<string, unknown>;
    const cid = cprops.parcela_id != null ? String(cprops.parcela_id) : '';
    if (!cid || cid === targetId) continue;

    const cbb = bboxOf(cand.geometry);
    if (bboxDistanceKm(targetBbox, cbb) > maxKm) continue;

    // Vertex-share check
    const cverts = collectVertices(cand.geometry, vertexTol);
    let shared = 0;
    for (const v of cverts) {
      if (targetVerts.has(v)) {
        shared++;
        if (shared >= minSharedVertices) break;
      }
    }
    if (shared < minSharedVertices) continue;

    const cCenter = bboxCenter(cbb);
    const b = bearingDeg(targetCenter, cCenter);
    const dir = bearingToCardinal(b);
    const delta = angularDelta(b, PERFECT[dir]);
    buckets[dir].push({ label: neighborLabel(cprops), delta });
  }

  const out: ColindanciasSpatialResult = {
    norte: null,
    sur: null,
    oriente: null,
    poniente: null,
  };
  let anyFound = false;
  for (const dir of ['norte', 'oriente', 'sur', 'poniente'] as const) {
    const items = buckets[dir].sort((a, b) => a.delta - b.delta).slice(0, maxPerSide);
    if (items.length > 0) {
      out[dir] = items.map((x) => x.label).join(' · ');
      anyFound = true;
    }
  }

  return anyFound ? out : undefined;
}
